/**
 * Active menu resolution.
 *
 * Resolves a single active menu item id from a NavMenuItem tree given the
 * current pathname. Guarantees only one item is highlighted, even when
 * multiple entries share the same path (legacy menu duplicates).
 *
 * Resolution priority (DFS):
 * 1. Exact path match — deepest first (child wins over parent)
 * 2. Dynamic prefix match (e.g. /person/:id matches /person) for leaf items
 *
 * @since 2026-04-27 [TMF9-53667]
 */

import { createContext, useContext } from 'react';
import { NavMenuItem } from '@/types/menu';

const HOME_LABELS = ['início', 'home', 'início do portal', 'portal'];
const HOME_EXCLUDED = ['voltar', 'status', 'venda'];
const DYNAMIC_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|edit|view|update|delete|new)$/i;

function isExactMatch(currentPath: string, item: NavMenuItem): boolean {
  if (item.path === '/') {
    const label = (item.label || '').toLowerCase().trim();
    if (HOME_EXCLUDED.some((e) => label.includes(e))) return false;
    if (label && !HOME_LABELS.some((h) => label.includes(h))) return false;
    return currentPath === '/';
  }
  return currentPath === item.path;
}

function isDynamicPrefixMatch(currentPath: string, item: NavMenuItem): boolean {
  if (!item.path || item.path === '/' || item.path === '#') return false;
  if (!currentPath.startsWith(`${item.path}/`)) return false;
  const remaining = currentPath.slice(item.path.length + 1);
  const nextSegment = remaining.split('/')[0];
  return DYNAMIC_SEGMENT.test(nextSegment);
}

interface Candidate {
  id: number;
  depth: number;
  exact: boolean;
}

function walk(
  items: NavMenuItem[],
  currentPath: string,
  depth: number,
  out: Candidate[]
): void {
  for (const item of items) {
    const hasChildren = item.children && item.children.length > 0;
    if (isExactMatch(currentPath, item)) {
      out.push({ id: item.id, depth, exact: true });
    } else if (!hasChildren && isDynamicPrefixMatch(currentPath, item)) {
      out.push({ id: item.id, depth, exact: false });
    }
    if (hasChildren) walk(item.children, currentPath, depth + 1, out);
  }
}

/**
 * Returns the id of the single menu item that should be marked active.
 * Returns null when no item matches.
 */
export function findActiveMenuId(
  items: NavMenuItem[] | undefined,
  currentPath: string
): number | null {
  if (!items || items.length === 0) return null;
  const candidates: Candidate[] = [];
  walk(items, currentPath, 0, candidates);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    if (a.exact !== b.exact) return a.exact ? -1 : 1;
    return b.depth - a.depth;
  });
  return candidates[0].id;
}

/**
 * Set of ancestor ids whose subtree contains the active item.
 * Used by MenuItem to render `hasActiveChild` styling.
 */
export function findAncestorIds(
  items: NavMenuItem[] | undefined,
  activeId: number | null
): Set<number> {
  const ancestors = new Set<number>();
  if (!items || activeId == null) return ancestors;

  const collect = (list: NavMenuItem[]): boolean => {
    for (const item of list) {
      if (item.id === activeId) return true;
      const children = item.children || [];
      if (children.length > 0 && collect(children)) {
        ancestors.add(item.id);
        return true;
      }
    }
    return false;
  };

  collect(items);
  return ancestors;
}

interface ActiveMenuContextValue {
  activeId: number | null;
  ancestorIds: Set<number>;
}

export const ActiveMenuContext = createContext<ActiveMenuContextValue>({
  activeId: null,
  ancestorIds: new Set(),
});

export function useActiveMenu(): ActiveMenuContextValue {
  return useContext(ActiveMenuContext);
}
